import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const mcpTrustTierSchema = z.enum([
  "verified",
  "community",
  "unverified",
  "blocked",
]);

export const mcpAppStatusSchema = z.enum([
  "active",
  "deprecated",
  "disabled",
  "blocked",
]);

export const mcpSourceTypeSchema = z.enum(["registry", "bundle", "manual"]);

export const mcpCatalogAppSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  name: z.string().min(1),
  publisher: z.string().min(1),
  description: z.string().nullable().optional(),
  icon_url: z.string().nullable().optional(),
  source_type: mcpSourceTypeSchema,
  trust_tier: mcpTrustTierSchema,
  status: mcpAppStatusSchema,
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

export const listMcpAppsSuccessSchema = z.object({
  apps: z.array(mcpCatalogAppSchema),
});

export const mcpAppVersionSchema = z.object({
  id: z.string().min(1),
  app_id: z.string().min(1),
  version: z.string().min(1),
  transport: z.string().nullable().optional(),
  entrypoint: z.string().nullable().optional(),
  capabilities_json: z.unknown().optional(),
  ui_resources_json: z.unknown().optional(),
  tools_json: z.unknown().optional(),
  signature_info_json: z.unknown().optional(),
  published_at: z.string().optional(),
});

export const getMcpAppSuccessSchema = z.object({
  app: mcpCatalogAppSchema,
  versions: z.array(mcpAppVersionSchema),
});

export type McpCatalogApp = z.infer<typeof mcpCatalogAppSchema>;
export type McpAppVersion = z.infer<typeof mcpAppVersionSchema>;
export type ListMcpAppsSuccess = z.infer<typeof listMcpAppsSuccessSchema>;
export type GetMcpAppSuccess = z.infer<typeof getMcpAppSuccessSchema>;

function buildAuthHeaders(accessToken?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return headers;
}

export function listMcpAppsPath(): string {
  return "/api/mcp/apps";
}

export function getMcpAppPath(appId: string): string {
  return `/api/mcp/apps/${encodeURIComponent(appId)}`;
}

export async function listMcpAppsRequest({
  apiBaseUrl,
  accessToken,
  q,
  trustTier,
  status,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken?: string;
  q?: string;
  trustTier?: z.infer<typeof mcpTrustTierSchema>;
  status?: z.infer<typeof mcpAppStatusSchema>;
  fetchImpl?: FetchLike;
}): Promise<ListMcpAppsSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const url = new URL(`${normalizeApiBaseUrl(apiBaseUrl)}${listMcpAppsPath()}`);
  if (q) url.searchParams.set("q", q);
  if (trustTier) url.searchParams.set("trust_tier", trustTier);
  if (status) url.searchParams.set("status", status);

  const response = await fetchFn(url.toString(), {
    method: "GET",
    headers: buildAuthHeaders(accessToken),
  });
  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to list MCP apps");
  }

  const payload = await response.json().catch(() => null);
  const parsed = listMcpAppsSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid MCP apps response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export async function getMcpAppRequest({
  apiBaseUrl,
  appId,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  appId: string;
  accessToken?: string;
  fetchImpl?: FetchLike;
}): Promise<GetMcpAppSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${getMcpAppPath(appId)}`,
    {
      method: "GET",
      headers: buildAuthHeaders(accessToken),
    },
  );
  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to fetch MCP app");
  }

  const payload = await response.json().catch(() => null);
  const parsed = getMcpAppSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid MCP app response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}
